import React, { useEffect, useState } from "react";
import {
  DatePicker,
  Flex,
  Item,
  Picker,
  LabeledValue,
  TimeField,
  View,
} from "@adobe/react-spectrum";
import { getLocalTimeZone, today } from "@internationalized/date";
import { FormData } from "../data";
import { DateTime } from "luxon";

/**
 *
 * Component to render a form with Date, Time and Timezone fields.
 * 
 * Layout: Two-row approach
 * - Row 1: Long descriptive label (e.g., "What time does the event start?")
 * - Row 2: Date, Time, and Timezone fields arranged horizontally
 */
export const DateTimeZoneFormFields = ({
  label,
  disableTimezone = true,
  selectedTimeZone,
  onChange,
  onTimezoneChange = null,
}) => {
  /** 
  // For Development: State with some initial values
  const [date, setDate] = useState(parseDate("2024-06-10"));
  const [time, setTime] = useState(new Time(11, 30));
  const [timezone, setTimezone] = useState("PT");
  */

  const [date, setDate] = useState(null);
  const [time, setTime] = useState(null);
  // Avoid undefined: React / Spectrum treat undefined as "uncontrolled"; later setting a key
  // triggers "changed from uncontrolled to controlled" (row 1 omits selectedTimeZone).
  const [timezone, setTimezone] = useState(() => selectedTimeZone ?? null);

  // When the timezone picker is disabled, rows 2–3 must use the event timezone from props
  // immediately. A separate effect that copied selectedTimeZone into local state ran *after*
  // the emit effect, so on the first commit after the user picked the event timezone,
  // returnDateTimeZone still saw timezone === null and skipped onChange — a one-frame race
  // that could leave startTime/endTime unset (often endTime if its effect ran in a bad order).
  const resolvedTimezone = disableTimezone
    ? (selectedTimeZone ?? null)
    : (timezone ?? null);

  const TIMEZONES = {
    PT: "America/Los_Angeles",
    AET: "Australia/Sydney",
    BT: "Europe/London",
    CET: "Europe/Berlin",
    GMT: "Etc/GMT",
    IST: "Asia/Kolkata",
  };

  function returnDateTimeZone(d,t,z) {
    if (d && t && z) {
      let dateTime = DateTime.fromISO(`${d}T${t}`, {
        zone: TIMEZONES[z],
      });

      onChange(dateTime, z);
    } 
  }

  useEffect(() => {
    returnDateTimeZone(date, time, resolvedTimezone);
  }, [date, time, resolvedTimezone]);

  return (
    <Flex direction="column" gap="size-50" width={"auto"}>
      {/* Row 1: Label */}
      <LabeledValue label={label}></LabeledValue>
      
      {/* Row 2: Date, Time, Timezone fields in horizontal layout */}
      <Flex direction="row" gap="size-200" alignItems="end">
        <View>
          {/* Date field*/}
          <DatePicker
            label="Date"
            isRequired
            necessityIndicator="icon"
            minWidth={"200px"}
            minValue={today(getLocalTimeZone())}
            value={date || null}
            onChange={(value) => {
              setDate(value);
            }}
          />
        </View>

      <View>
        {/* Time field*/}
        <TimeField
          label="&nbsp;"
          shouldForceLeadingZeros={true}
          granularity="minute"
          value={time || null}
          onChange={(value) => {
            // Round to nearest 15 minutes
            const roundedMinute = Math.floor(value.minute / 15) * 15;
            value.minute = roundedMinute;
            setTime(value);
          }}
        />
      </View>

      <View>
        
        <Picker
          label="&nbsp;"
          placeholder="Select a timezone"
          items={FormData.timezoneOptions}
          selectedKey={resolvedTimezone ?? null}
          isDisabled={disableTimezone}
          onSelectionChange={(value) => {
            if (!disableTimezone) {
              setTimezone(value);             
            }
          }}
        >
          {(item) => <Item key={item.key}>{item.name}</Item>}
        </Picker>
      </View>
      </Flex>
    </Flex>
  );
};
